// Copyright 2020 Sorama B.V.

import {
    autoinject,
    customElement,
    observable,
    BindingEngine,
    Disposable,
    bindable,
    bindingMode,
} from "aurelia-framework";
import { ProjectDTO } from "datatypes/SoramaWebApi";
import { ProjectCanvasCollectionDTO } from "datatypes/SoramaWebApi";
import { StatusItem } from "datatypes/Status";
import { IPagedRequest } from "libraries/WebRequestHandler/IWebRequestHandler";
import { ProjectRepository } from "repositories/ProjectRepository";
import { IProjectManagementService } from "services/Project/IProjectManagementService";

@customElement("canvas-dropdown")
@autoinject
export class CanvasDropdown {
    //#region Private Properties
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public selectedItem: ProjectCanvasCollectionDTO;

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public canvasType: "presentation" | "journal";

    @observable
    private selectedProject: ProjectDTO;
    private subscription: Disposable;

    @observable
    protected pagedRequest: IPagedRequest<ProjectCanvasCollectionDTO>;

    @observable
    private filterText: string;
    //#endregion

    @bindable({ defaultBindingMode: bindingMode.oneWay, defaultValue: true })
    canCreate: boolean;

    //#region Constructor and Aurelia Callbacks
    constructor(
        private readonly projectManagementService: IProjectManagementService,
        private readonly projectRepository: ProjectRepository,
        private readonly bindingEngine: BindingEngine
    ) {}

    public bind(): void {
        this.subscription = this.bindingEngine
            .propertyObserver(this.projectManagementService, "currentProject")
            .subscribe(this.currentProjectChanged);
        this.currentProjectChanged(this.projectManagementService.currentProject);
    }

    public unbind(): void {
        this.subscription.dispose();
        this.subscription = null;
    }

    public currentProjectChanged = (newValue: StatusItem<ProjectDTO>): void => {
        if (!newValue.status.available || newValue.item == undefined) {
            return;
        }

        if (this.canvasType === "presentation") {
            this.selectedProject = newValue.item;
            this.pagedRequest = this.projectRepository.getPresentations(newValue.item.id);
        }
        if (this.canvasType === "journal") {
            this.selectedProject = newValue.item;
            this.pagedRequest = this.projectRepository.getJournals(newValue.item.id);
        }
    };

    public filterTextChanged(newFilterText: string): void {
        if (
            !this.projectManagementService.currentProject.status.available ||
            this.projectManagementService.currentProject.item == undefined
        ) {
            return;
        }

        if (this.canvasType === "presentation") {
            this.pagedRequest = this.projectRepository.getPresentations(
                this.projectManagementService.currentProject.item.id,
                newFilterText ? "$filter=contains(Name,'" + newFilterText + "')" : undefined
            );
        }
        if (this.canvasType === "journal") {
            this.pagedRequest = this.projectRepository.getJournals(
                this.projectManagementService.currentProject.item.id,
                newFilterText ? "$filter=contains(Name,'" + newFilterText + "')" : undefined
            );
        }
    }

    protected createNew(name: string): Promise<void> {
        if (
            !this.projectManagementService.currentProject.status.available ||
            this.projectManagementService.currentProject.item == undefined ||
            !name
        ) {
            return;
        }

        const promise =
            this.canvasType === "presentation"
                ? this.projectRepository.createPresentation(this.projectManagementService.currentProject.item.id, name)
                : this.projectRepository.createJournal(this.projectManagementService.currentProject.item.id, name);

        return promise.then((x) => {
            this.selectedItem = x;
            this.currentProjectChanged(this.projectManagementService.currentProject);
        });
    }
}
